//
// Copyright © 2026 PlayJoy Studios.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// Video timecodes. A timecode is an ordinary link to the video file with `#t=<seconds>`:
// the server, search, notifications and other clients already understand links, and a browser
// opened outside Huly starts the video at that second by itself. Inside Huly the click opens the
// video in the preview popup at that moment.
//
// The link names the file by its blob id, which the browser generates before the first byte is
// sent, so a timecode can be written while the video is still uploading.

import { type Blob as PlatformBlob, type Ref } from '@hcengineering/core'
import { getMetadata } from '@hcengineering/platform'
import { showPopup } from '@hcengineering/ui'
import { get } from 'svelte/store'
import FilePreviewPopup from './components/FilePreviewPopup.svelte'
import MessageBox from './components/MessageBox.svelte'
import { getCurrentWorkspaceUuid, getFileUrl } from './file'
import { getLocalVideoUrl, type LocalVideo, localVideos } from './local-video'
import presentation from './plugin'
import { formatTimecode } from './timecode-format'

export { formatTimecode, getTimecodeLabel, parseTimecode } from './timecode-format'

/** @public */
export interface Timecode {
  file: Ref<PlatformBlob>
  name: string
  seconds: number
}

/**
 * Absolute link to a workspace file, as stored in embeds and timecodes.
 * @public
 */
export function makeFileUrl (file: Ref<PlatformBlob>, name: string): string {
  return new URL(getFileUrl(file, name), window.location.href).href
}

/** @public */
export function makeTimecodeUrl (file: Ref<PlatformBlob>, name: string, seconds: number): string {
  const url = new URL(makeFileUrl(file, name))
  url.hash = `t=${Math.max(0, Math.floor(seconds))}`
  return url.href
}

/**
 * Recognizes a link to a file of the current workspace; `seconds` is set for a timecode (`#t=`).
 * @public
 */
export function parseFileUrl (href: string): (Omit<Timecode, 'seconds'> & { seconds?: number }) | undefined {
  let url: URL
  try {
    url = new URL(href, window.location.href)
  } catch {
    return undefined
  }

  const file = url.searchParams.get('file')
  if (file === null || file === '') return undefined
  if (url.searchParams.get('workspace') !== getCurrentWorkspaceUuid()) return undefined

  const filesOrigin = new URL(getFileUrl(file), window.location.href).origin
  if (url.origin !== filesOrigin) return undefined

  const time = /^#t=(\d+(?:\.\d+)?)$/.exec(url.hash)
  if (url.hash !== '' && time === null) return undefined

  const lastSegment = url.pathname.split('/').pop() ?? ''
  let name = file
  try {
    name = decodeURIComponent(lastSegment) !== '' ? decodeURIComponent(lastSegment) : file
  } catch {}

  return { file: file as Ref<PlatformBlob>, name, seconds: time !== null ? Number(time[1]) : undefined }
}

/**
 * Recognizes a timecode link to a file of the current workspace.
 * @public
 */
export function parseTimecodeUrl (href: string): Timecode | undefined {
  const link = parseFileUrl(href)
  if (link?.seconds === undefined) return undefined
  return { file: link.file, name: link.name, seconds: link.seconds }
}

/** @public */
export interface TimecodeOpenOptions {
  // called with the current player position instead of copying a link
  onPickTime?: (seconds: number) => void
}

function showVideo (
  source: string,
  timecode: Timecode,
  contentType: string,
  options: TimecodeOpenOptions,
  local?: LocalVideo
): void {
  showPopup(
    FilePreviewPopup,
    {
      file: source,
      name: timecode.name,
      contentType,
      fullSize: false,
      showIcon: false,
      props: {
        startTime: timecode.seconds,
        timecodeFile: timecode.file,
        localUploadProgress: local?.state === 'uploading' ? Math.round(local.progress) : undefined,
        onPickTime: options.onPickTime
      }
    },
    'centered'
  )
}

/**
 * Upload in parts that another person is running now (front server keeps its state on disk).
 * @public
 */
export async function getVideoUploadStatus (file: string): Promise<{ received: number, size: number } | undefined> {
  try {
    const url = new URL(getFileUrl(file), window.location.href)
    url.pathname = url.pathname.replace(/\/[^/]*$/, `/multipart/${encodeURIComponent(file)}/status`)
    url.search = ''
    const token = getMetadata(presentation.metadata.Token) ?? ''
    const response = await fetch(url.href, { headers: { Authorization: `Bearer ${token}` } })
    if (!response.ok) return undefined
    const status = await response.json()
    return status.state === 'uploading' ? { received: status.received, size: status.size } : undefined
  } catch {
    return undefined
  }
}

/**
 * Opens the video of a timecode: the uploaded file, the author's local copy while it uploads,
 * or a message when the file is not there.
 * @public
 */
export async function showTimecode (timecode: Timecode, options: TimecodeOpenOptions = {}): Promise<void> {
  const local = get(localVideos).get(timecode.file)
  if (local?.state === 'uploading') {
    showVideo(getLocalVideoUrl(timecode.file) ?? '', timecode, local.type, options, local)
    return
  }

  let response: Response | undefined
  try {
    response = await fetch(getFileUrl(timecode.file, timecode.name), { method: 'HEAD', credentials: 'include' })
  } catch {}

  if (response?.ok === true) {
    const contentType = response.headers.get('content-type') ?? local?.type ?? 'video/mp4'
    showVideo(timecode.file, timecode, contentType, options)
    return
  }

  if (local !== undefined && local.state === 'uploaded') {
    showVideo(getLocalVideoUrl(timecode.file) ?? '', timecode, local.type, options)
    return
  }

  const status = await getVideoUploadStatus(timecode.file)
  if (status !== undefined) {
    showPopup(MessageBox, {
      label: presentation.string.Timecode,
      message: presentation.string.VideoUploadingProgress,
      params: {
        name: timecode.name,
        time: formatTimecode(timecode.seconds),
        percent: status.size > 0 ? Math.floor((status.received / status.size) * 100) : 0
      },
      canSubmit: false
    })
    return
  }

  showPopup(MessageBox, {
    label: presentation.string.Timecode,
    message: presentation.string.VideoNotUploaded,
    params: { name: timecode.name, time: formatTimecode(timecode.seconds) },
    canSubmit: false
  })
}

/**
 * Click handler for links: opens a timecode inside Huly. Returns false for other links.
 * @public
 */
export function openTimecodeLink (href: string): boolean {
  const timecode = parseTimecodeUrl(href)
  if (timecode === undefined) return false
  void showTimecode(timecode)
  return true
}
